import React, { useState, useEffect } from 'react';
import CommonTable from '../../components/commonComponent/CommanTable';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import axios from '../../utils/axiosInstance';
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa';
import { hasAccess } from '../../utils/permissionHelper';
import { confirmDelete, toastSuccess, toastError } from '../../utils/alertHelper';

const tabHoverStyle = {
  cursor: 'pointer',
  transition: 'background 0.2s, color 0.2s'
  
};
// Tab Config
const tabConfig = [
  {
    key: 'projectType',
    label: 'Project Type',
    addLabel: 'Add New Project Type',
    columns: [
      { key: 'srNo', label: 'Sr.no:' },
      { key: 'name', label: 'Project Type Name' },
      { key: 'actions', label: 'Action' },
    ],
    modalFields: [
      { name: 'name', label: 'Project Type Name', type: 'text', placeholder: 'Enter Project Type Name' },
    ],
    initialValues: { name: '' },
    requiredFields: ['name'],
    type: 'Project Settings',
    apiKey: 'projectType',
  },
  {
    key: 'leadSource',
    label: 'Lead Source',
    addLabel: 'Add New Lead Source',
    columns: [
      { key: 'srNo', label: 'Sr.no:' },
      { key: 'name', label: 'Lead Source Name' },
      { key: 'actions', label: 'Action' },
    ],
    modalFields: [
      { name: 'name', label: 'Lead Source Name', type: 'text', placeholder: 'Enter Lead Source Name' },
    ],
    initialValues: { name: '' },
    requiredFields: ['name'],
    type: 'Lead Source Settings',
    apiKey: 'leadSource',
  },
  {
    key: 'dealStage',
    label: 'Deal Stage',
    addLabel: 'Add New Deal Stage',
    columns: [
      { key: 'srNo', label: 'Sr.no:' },
      { key: 'name', label: 'Deal Stage Name' },
      { key: 'actions', label: 'Action' },
    ],
    modalFields: [
      { name: 'name', label: 'Deal Stage Name', type: 'text', placeholder: 'Enter Deal Stage Name' },
    ],
    initialValues: { name: '' },
    requiredFields: ['name'],
    type: 'Deal Stage Settings',
    apiKey: 'dealStage',
  },
  {
    key: 'dealType',
    label: 'Deal Type',
    addLabel: 'Add New Deal Type',
    columns: [
      { key: 'srNo', label: 'Sr.no:' },
      { key: 'name', label: 'Deal Type Name' },
      { key: 'actions', label: 'Action' },
    ],
    modalFields: [
      { name: 'name', label: 'Deal Type Name', type: 'text', placeholder: 'Enter Deal Type Name' },
    ],
    initialValues: { name: '' },
    requiredFields: ['name'],
    type: 'Deal Type Settings',
    apiKey: 'dealType',
  },
  {
    key: 'dealTemperature',
    label: 'Deal Temperature',
    addLabel: 'Add New Deal Temperature',
    columns: [
      { key: 'srNo', label: 'Sr.no:' },
      { key: 'name', label: 'Deal Temperature Name' },
      { key: 'actions', label: 'Action' },
    ],
    modalFields: [
      { name: 'name', label: 'Deal Temperature Name', type: 'text', placeholder: 'Enter Deal Temperature Name' },
    ],
    initialValues: { name: '' },
    requiredFields: ['name'],
    type: 'Deal Temperature Settings',
    apiKey: 'dealTemperature',
  },
];

// API Map
const apiMap = {
  projectType: '/admin/api/project-type',
  leadSource: '/admin/api/lead-source',
  dealStage: '/admin/api/deal-stage',
  dealType: '/admin/api/deal-type',
  dealTemperature: '/admin/api/deal-temprature',
};

function LeadManage() {
  const user = JSON.parse(localStorage.getItem("user"));
  const business = localStorage.getItem("businessId") || "";

  // Page-level access (ANY one permission allows page access)
  const canAccessPage =
    hasAccess(user, "Project Settings") ||
    hasAccess(user, "Lead Source Settings") ||
    hasAccess(user, "Deal Type Settings") ||
    hasAccess(user, "Deal Stage Settings") ||
    hasAccess(user, "Deal Temperature Settings");

  const [activeTab, setActiveTab] = useState(tabConfig[0].key);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(tabConfig[0].initialValues);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDefaultLoading, setIsDefaultLoading] = useState(false);
  const defaultLimit = process.env.REACT_APP_PAGE_LIMIT ? parseInt(process.env.REACT_APP_PAGE_LIMIT, 10) : 10;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);

  const currentTab = tabConfig.find(t => t.key === activeTab);

  // Tab permissions
  const canViewTab = hasAccess(user, currentTab.type);
  const canAdd = hasAccess(user, currentTab.type, "Create");
  const canEdit = hasAccess(user, currentTab.type, "Edit");
  const canDelete = hasAccess(user, currentTab.type, "Delete");

  useEffect(() => {
    fetchTabData(activeTab);
  }, [activeTab]);

  async function fetchTabData(tabKey) {
    setLoading(true);
    try {
      const res = await axios.get(`${apiMap[tabKey]}?business=${business}`);

      let items = [];
      if (tabKey === 'projectType') items = res.data.projectTypes || [];
      if (tabKey === 'leadSource') items = res.data.leadSources || [];
      if (tabKey === 'dealStage') items = res.data.dealStages || [];
      if (tabKey === 'dealType') items = res.data.dealTypes || [];
      if (tabKey === 'dealTemperature') items = res.data.dealTempratures || [];

      setData(prev => ({
        ...prev,
        [tabKey]: items.map((item, idx) => ({ srNo: idx + 1, ...item }))
      }));
    } catch (err) {
      setData(prev => ({ ...prev, [tabKey]: [] }));
    } finally {
      setLoading(false);
    }
  }

  // Table rows with permission-controlled actions
  const rows = (data[activeTab] || [])
    .filter(r => r.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    .map((row, idx) => ({
      ...row,
      actions: (
        <div>
          <a
            className="btn-sm me-1 py-0 px-1 border-0 text-primary-custom"
            href="#"
            style={!canEdit ? { pointerEvents: 'none', opacity: 0.4 } : {}}
            onClick={e => {
              e.preventDefault();
              if (canEdit) handleEdit(idx);
            }}
          >
            <FaPencilAlt size={14} />
          </a>

          <a
            className="btn-sm py-0 px-1 border-0 text-danger"
            href="#"
            style={!canDelete ? { pointerEvents: 'none', opacity: 0.4 } : {}}
            onClick={e => {
              e.preventDefault();
              if (canDelete) handleDelete(idx);
            }}
          >
            <FaTrashAlt size={14} />
          </a>
        </div>
      )
    }));

  function handleAdd() {
    setModalData(currentTab.initialValues);
    setErrors({});
    setShowModal(true);
  }

  function handleEdit(idx) {
    if (!canEdit) return;
    const item = data[activeTab]?.[idx];
    if (!item) return;

    const { srNo, actions, ...raw } = item;
    setModalData(raw);
    setErrors({});
    setShowModal(true);
  }

  async function handleDelete(idx) {
    if (!canDelete) return;
    const item = data[activeTab]?.[idx];
    if (!item?._id) return;

    try {
      const res = await confirmDelete(`Are you sure you want to delete ${item.name || ''}?`);
      if (!res.isConfirmed) return;

      await axios.delete(`${apiMap[activeTab]}/${item._id}?business=${business}`);
      toastSuccess('Deleted successfully');
      fetchTabData(activeTab);
    } catch (err) {
      console.error('Delete failed', err);
      toastError('Failed to delete. Please try again.');
    }
  }

  async function handleSubmit() {
    const newErrors = {};
    currentTab.requiredFields.forEach(f => {
      if (!modalData[f]) newErrors[f] = "Required";
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const { srNo, actions, ...payload } = modalData;
    payload.business = business;

    try {
      if (modalData._id) {
        await axios.put(`${apiMap[activeTab]}/${modalData._id}`, payload);
      } else {
        await axios.post(apiMap[activeTab], payload);
      }

      setShowModal(false);
      fetchTabData(activeTab);
    } catch (err) {}
  }

  async function handleDefault() {
    setIsDefaultLoading(true);
    try {
      await axios.post('/admin/api/default', {
        business,
        type: currentTab.apiKey
      });
      fetchTabData(activeTab);
    } finally {
      setIsDefaultLoading(false);
    }
  }

  if (!canAccessPage) {
    return (
      <div className="container py-5 text-center">
        <h1 className="text-danger">403 Forbidden</h1>
        <p>You do not have permission to access Lead Manage.</p>
      </div>
    );
  }

  if (!canViewTab) {
    return (
      <div className="container py-5 text-center">
        <h4 className="text-warning">
          You do not have permission for: {currentTab.label}
        </h4>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">

      {/* Header Area */}
      <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
        <h2 className="h2 mb-0">Lead Manage</h2>

        <div className="d-flex align-items-center" style={{ gap: '15px' }}>

          {canAdd && (
            <button className="btn btn-primary" onClick={handleAdd}>
              + {currentTab.addLabel}
            </button>
          )}

          {/* Add default */}
          {(data[activeTab]?.length === 0 && !loading && canAdd) && (
            <button
              className="btn btn-outline-primary btn-sm"
              disabled={isDefaultLoading}
              onClick={handleDefault}
              style={{ minWidth: 110 }}
            >
              {isDefaultLoading
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Adding...</>
                : 'Add Default'}
            </button>
          )}

          {/* Search */}
          

        </div>
      </div>

      {/* Tabs */}
      <div className="d-flex align-items-center justify-content-between mb-3 nav-tabs">
        <ul className="nav mb-0">
          {tabConfig.map(tab => {
            if (
              !hasAccess(user, tab.type, "List") &&
              !hasAccess(user, tab.type, "View")
            ) return null;

            return (
              <li className="nav-item text-muted" key={tab.key}>
                <button
                  className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                  style={{
                    ...tabHoverStyle,
                    backgroundColor: activeTab === tab.key ? '#5766A1' : '',
                    color: activeTab === tab.key ? '#ffffff' : '#212529',
                    border: activeTab === tab.key ? '1px solid #5766A1' : '1px solid transparent',
                    fontWeight: activeTab === tab.key ? '600' : '500'
                  }}
                  onMouseEnter={e => {
                    if (activeTab !== tab.key) {
                      e.currentTarget.style.background = '#e9ecef';
                      e.currentTarget.style.color = '#5766A1';
                    }
                  }}
                  onMouseLeave={e => {
                    if (activeTab !== tab.key) {
                      e.currentTarget.style.background = '';
                      e.currentTarget.style.color = '#212529';
                    }
                  }}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label} {activeTab === tab.key}
                </button>
              </li>
            );
          })}
        </ul>
        {/* Search box on right */}
        <div className="input-group mb-2" style={{ maxWidth: 280, borderRadius: "6px", overflow: "hidden" }}>
          <span className="input-group-text bg-white border-end-0" style={{ borderRadius: "6px 0 0 6px" }}>
            <i className="fas fa-search text-muted"></i>
          </span>
          <input
            type="text"
            className="form-control border-start-0"
            placeholder="Type to search..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ boxShadow: "none", borderRadius: "0 6px 6px 0" }}
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary"></div>
        </div>
      ) : (
        <CommonTable
          columns={currentTab.columns}
          data={rows}
          itemsPerPageOptions={[15, 20, 25, 30]}
          defaultItemsPerPage={15}
          page={page}
          onPageChange={setPage}
          itemsPerPage={limit}
          onItemsPerPageChange={setLimit}
        />
      )}

      {/* Modal */}
      <CommonModalForm
        visible={showModal}
        title={modalData._id ? `Edit ${currentTab.label}` : `Add New ${currentTab.label}`}
        fields={currentTab.modalFields}
        values={modalData}
        onChange={setModalData}
        onCancel={() => setShowModal(false)}
        onSubmit={handleSubmit}
        errors={errors}
        requiredFields={currentTab.requiredFields}
        submitLabel={modalData._id ? "Update" : "Save"}
        cancelLabel="Cancel"
      />
    </div>
  );
}

export default LeadManage;
